import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Cards, Crumbs, Thumb } from "../components/Bits";
import { CookMode, useCookOpen } from "../components/CookMode";
import { TimerDock, useTimers } from "../components/CookTimers";
import { Head } from "../components/Head";
import { NutritionBox, RateBox, StarsShown } from "../components/Rating";
import { ShareMenu } from "../components/Share";
import { useData } from "../lib/data";
import { API, addToPlan, amount, origin, store, toast } from "../lib/util";
import NotFound from "./NotFound";


function WakeButton() {
  const lock = useRef(null);
  const [on, setOn] = useState(false);
  const supported = "wakeLock" in navigator;
  const click = async () => {
    try {
      if (lock.current) { await lock.current.release(); lock.current = null; setOn(false); }
      else { lock.current = await navigator.wakeLock.request("screen"); setOn(true); lock.current.addEventListener("release", () => { lock.current = null; }); }
    } catch (e) { toast("Kunne ikke holde skærmen tændt"); }
  };
  return <button className="btn ghost" type="button" id="wake" aria-pressed={String(on)} hidden={!supported} onClick={click} data-testid="recipe-wake-button">{on ? "Skærmen forbliver tændt" : "Hold skærmen tændt"}</button>;
}

function RecipeView({ r, recipes }) {
  const base = r.base || 4;
  const [n, setN] = useState(() => Math.min(24, Math.max(1, +store.get("portions", base) || base)));
  const [done, setDone] = useState({});
  const [sdone, setSdone] = useState({});
  const [ld, setLd] = useState(null);
  const [cooking, openCook, closeCook] = useCookOpen();
  const timers = useTimers();
  useEffect(() => { store.set("portions", n); }, [n]);
  const loadLd = useCallback(() => fetch(`${API}/recipes/${r.slug}/jsonld`).then(x => (x.ok ? x.json() : null)).then(setLd).catch(() => {}), [r.slug]);
  useEffect(() => { loadLd(); }, [loadLd]);
  useEffect(() => {
    const h = window.location.hash;
    if (!h.startsWith("#trin-")) return;
    const go = () => document.getElementById(h.slice(1))?.scrollIntoView({ block: "start" });
    const ts = [50, 400, 1200].map(ms => setTimeout(go, ms));
    return () => ts.forEach(clearTimeout);
  }, []);
  const setP = v => setN(v);
  const common = x => x.tags.filter(t => r.tags.includes(t)).length;
  const related = recipes.filter(x => x.slug !== r.slug)
    .sort((a, b) => (common(b) - common(a)) || (Math.abs(a.price - r.price) - Math.abs(b.price - r.price))).slice(0, 3);
  const bc = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Opskrifter", item: `${origin()}/opskrifter/` },
    { "@type": "ListItem", position: 2, name: r.title }] };

  return (
    <>
      <Head title={`${r.title}: ${r.time} min, ca. ${r.price} kr. pr. person`} desc={r.intro} path={`opskrifter/${r.slug}/`} ogType="article" image={origin() + r.og_image} ld={ld ? [ld, bc] : [bc]} />
      <Crumbs trail={[["opskrifter/", "Opskrifter"], [null, r.title]]} />
      <article className="wrap" id="recipe" data-base={base} data-price={r.price} data-slug={r.slug}>
        <div className="rtop"><div>
          <h1 data-testid="recipe-title">{r.title}</h1><p className="lead">{r.intro}</p>
          <div className="pills" data-testid="recipe-pills">
            <span className="pill" data-testid="recipe-time">{r.time} min</span><span className="pill" data-testid="recipe-diff">{r.diff}</span>
            {r.tags.map(t => <span key={t} className={"pill" + (t === "Vegetar" ? " g" : "")}>{t}</span>)}
          </div>
          {r.rating_avg != null && <p className="ravg top"><StarsShown avg={r.rating_avg} count={r.rating_count} testid="recipe-rating-summary" /></p>}
          <div className="acts noprint">
            <button className="btn cookbtn" type="button" onClick={openCook} data-testid="cook-start-button">Start madlavning</button>
            <button className="btn" type="button" id="addPlan" onClick={() => addToPlan(r.slug, n)} data-testid="recipe-add-plan-button">Føj til madplan</button>
            <button className="btn ghost" type="button" id="print" onClick={() => window.print()} data-testid="recipe-print-button">Print</button>
            <ShareMenu url={`${origin()}/api/del/opskrift/${r.slug}`} title={r.title} testid="recipe-share" />
            <WakeButton />
          </div>
        </div>
          <Thumb r={r}><div className="sticker" data-testid="recipe-price-sticker"><b>{r.price}</b><small>kr. pr. person</small></div></Thumb>
        </div>
        <div className="rbody">
          <aside className="ingbox"><h2>Ingredienser</h2>
            <div className="stepper"><span id="pl-label">Portioner</span>
              <button type="button" id="pm" aria-label="Færre portioner" onClick={() => setP(Math.max(1, n - 1))} data-testid="portions-minus">−</button>
              <b id="pn" aria-live="polite" data-testid="portions-count">{n}</b>
              <button type="button" id="pp" aria-label="Flere portioner" onClick={() => setP(Math.min(24, n + 1))} data-testid="portions-plus">+</button>
              <small>Pris: <b id="pcost" data-testid="portions-cost">{`ca. ${Math.round(r.price * n)} kr.`}</b></small></div>
            <ul className="ing" id="ing" data-testid="ingredient-list">
              {r.ingredients.map((i, k) => (
                <li key={k} className={done[k] ? "done" : undefined} data-amt={i.amount ?? undefined} data-unit={i.amount !== null ? i.unit : undefined}
                  onClick={() => setDone({ ...done, [k]: !done[k] })} data-testid={`ingredient-${k}`}>
                  <span className="amt">{i.amount !== null ? amount(i.amount * n / base, i.unit) : ""}</span><span>{i.name}</span>
                </li>
              ))}
            </ul>
            <NutritionBox n={r.nutrition} />
          </aside>
          <div><h2>Fremgangsmåde</h2>
            <p className="cookcta noprint"><button className="btn cookbtn" type="button" onClick={openCook} data-testid="cook-start-button-steps">Start madlavning</button><small>Store bogstaver, ét trin ad gangen og timere</small></p>
            <ol className="steps" data-testid="steps-list">{r.steps.map((s, k) => <li key={k} id={`trin-${k + 1}`} className={sdone[k] ? "done" : undefined} onClick={() => setSdone({ ...sdone, [k]: !sdone[k] })} data-testid={`step-${k + 1}`}>{s}</li>)}</ol>
            {r.tip && <div className="tip"><b>Tip:</b> {r.tip}</div>}
            <IngredientLinks slug={r.slug} />
            <RateBox key={cooking ? "c" : "p"} slug={r.slug} initial={{ count: r.rating_count, avg: r.rating_avg, shown: r.rating_avg != null }} onChange={loadLd} />
          </div>
        </div>
        <section className="related"><h2>Flere opskrifter</h2><Cards rs={related} /></section>
      </article>
      {cooking ? <CookMode r={r} n={n} timers={timers} onClose={closeCook} onRated={loadLd} /> : <TimerDock timers={timers} floating />}
    </>
  );
}

function IngredientLinks({ slug }) {
  const ps = useData().ingredients.filter(p => p.recipe_slugs.includes(slug));
  if (!ps.length) return null;
  return (
    <div className="quick ilinks" data-testid="recipe-ingredient-links">
      {ps.map(p => <a key={p.slug} href={`/opskrifter-med/${p.slug}/`} data-testid={`recipe-ingredient-link-${p.slug}`}>Flere opskrifter med {p.name}</a>)}
    </div>
  );
}

export default function Recipe() {
  const { slug } = useParams();
  const { BY, recipes } = useData();
  const r = BY[slug];
  if (!r) return <NotFound />;
  return <RecipeView key={slug} r={r} recipes={recipes} />;
}
